import type { PmDoc, PmInline, PmNode } from "@storytell/types/richText";
import { PmNodeType } from "@storytell/types/richText";

/**
 * Serializes inline content to plain text (no markdown).
 * Used to produce the string sent to the LLM from a PmDoc.
 */
function inlinesToText(inlines: PmInline[] | undefined): string {
  if (!inlines?.length) return "";
  return inlines
    .map((node) => {
      if (node.type === PmNodeType.Text) return node.text ?? "";
      if (node.type === PmNodeType.VariableV1) return (node.attrs as { text?: string }).text ?? "";
      if (node.type === PmNodeType.TemplateVarV1) {
        const attrs = node.attrs;
        const { varType, value, fallback } = attrs;
        const fallbackText = fallback != null && fallback !== "" ? fallback : "";
        if (varType === "text" || varType === "paragraph") {
          const t = (value as { text?: string })?.text ?? "";
          return t.trim() ? t : fallbackText;
        }
        if (varType === "dropdown") return (value as { value?: string })?.value ?? fallbackText;
        if (varType === "file-pick" || varType === "record-audio") {
          const v = value as { assetId?: string; filename?: string };
          return v?.assetId ? (v.filename ?? "") : fallbackText;
        }
        return fallbackText;
      }
      if (node.type === PmNodeType.PastedTextV1) return node.attrs.text ?? "";
      if (node.type === PmNodeType.HardBreak) return "\n";
      return "";
    })
    .join("");
}

function blockToText(node: PmNode): string {
  if (node.type === PmNodeType.Paragraph) {
    return inlinesToText(node.content);
  }
  // A heading's children are inline, so the generic content walk below would
  // recurse into text nodes and drop every one of them.
  if (node.type === PmNodeType.Heading) {
    return inlinesToText(node.content);
  }
  if (node.type === PmNodeType.CodeBlock) {
    return inlinesToText(node.content);
  }
  if (node.type === PmNodeType.BulletList) {
    return node.content
      .map((item) => {
        const itemContent = item.content;
        const para = itemContent?.[0];
        const text = para && para.type === PmNodeType.Paragraph ? inlinesToText(para.content) : "";
        return `- ${text}`;
      })
      .join("\n");
  }
  if (node.type === PmNodeType.OrderedList) {
    return node.content
      .map((item, i) => {
        const itemContent = item.content;
        const para = itemContent?.[0];
        const text = para && para.type === PmNodeType.Paragraph ? inlinesToText(para.content) : "";
        return `${i + 1}. ${text}`;
      })
      .join("\n");
  }
  if (node.type === PmNodeType.Blockquote && "content" in node && Array.isArray(node.content)) {
    return node.content
      .map((child) => blockToText(child))
      .filter(Boolean)
      .join("\n");
  }
  if ("content" in node && Array.isArray(node.content)) {
    return (node.content as PmNode[])
      .map((child) => blockToText(child))
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

/**
 * Serializes a PmDoc to plain text (paragraphs and lists with newlines).
 * Template vars are expanded to their value or fallback.
 */
export function getTextFromDoc(doc: PmDoc): string {
  if (!doc.content?.length) return "";
  return doc.content
    .map((node) => blockToText(node))
    .filter(Boolean)
    .join("\n");
}
